// Helpers for the lazily-loaded AWS Organizations tree in the hierarchy view.
//
// The tree is fetched one level at a time (POST /org/children), so the client
// only ever holds the levels the user has actually expanded. Anything that used
// to walk a fully-materialised tree has to work off that partial cache instead.

/**
 * Collects every organizational unit currently loaded into the children cache,
 * for the "Move Account" destination picker.
 *
 * This is deliberately limited to what has been loaded. The old version walked a
 * complete tree, so the picker could offer every OU in the organization; the
 * whole reason the tree is lazy now is that materialising it costs two AWS calls
 * per node and times out at the gateway on real customer orgs. Offering only
 * loaded OUs is the honest trade — the UI tells the user to expand more of the
 * tree rather than silently presenting a short list as if it were complete.
 *
 * @param {{id: string, name?: string}|null} root the root node, or null before load
 * @param {Object<string, Array<{id: string, name: string, type: string}>>} childrenByParent
 * @returns {Array<{id: string, name: string, type: string}>} root first, then OUs, deduped by id
 */
export function collectLoadedOUs(root, childrenByParent) {
  const byId = new Map();

  if (root && root.id) {
    byId.set(root.id, { id: root.id, name: root.name || 'Root', type: 'ROOT' });
  }

  Object.values(childrenByParent || {}).forEach((children) => {
    (children || []).forEach((node) => {
      if (node && node.type === 'OU' && node.id && !byId.has(node.id)) {
        byId.set(node.id, { id: node.id, name: node.name, type: 'OU' });
      }
    });
  });

  return Array.from(byId.values());
}
